import React, { useEffect, useState } from 'react';

import { Skeleton } from '@/components/ui/skeleton';
import { useAxios } from '@/hooks/useAxios';
import { SettingEditableCard } from '@/sections/settings/components';
import { AllowedDomainsEditForm } from '@/sections/settings/components/forms/AllowedDomainsEditForm';
import { useAuth } from '@/state/auth';

interface AllowedDomainsSectionProps {
  email?: string;
  editable?: boolean;
}

export const AllowedDomainsSection: React.FC<AllowedDomainsSectionProps> = ({
  email,
  editable = true,
}) => {
  const { me, friend } = useAuth();
  const currentEmail = email || friend || me;
  const axios = useAxios({ email: currentEmail, doNotImpersonate: true });

  const [domains, setDomains] = useState<string[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [fetchFailed, setFetchFailed] = useState(false);

  useEffect(() => {
    axios
      .get<{ domains: string[] }>('/customer/domains')
      .then(res => {
        setDomains(res.data.domains ?? []);
        setLoaded(true);
      })
      .catch(() => {
        setFetchFailed(true);
        setLoaded(true);
      });
  }, [currentEmail]);

  return (
    <SettingEditableCard
      data-testid="allowed-domains-card"
      editConfig={{
        modalTitle: 'Allowed Email Domains',
        formComponent: AllowedDomainsEditForm,
        formProps: {
          currentEmail,
          editable: editable && !fetchFailed,
          onDomainsChange: setDomains,
        },
        hideFooter: false,
        modalSize: 'lg',
        footer: {
          text: 'Save',
          form: 'allowed-domains-form',
        },
      }}
      editable={editable}
    >
      <div className="flex flex-col gap-1">
        <h4 className="font-bold">Allowed Email Domains</h4>
        <span className="text-chariot-text-secondary text-xs">
          Control which email domains can be invited
        </span>
        {!loaded ? (
          <Skeleton className="mt-1 h-3 w-32" />
        ) : (
          <span className="text-chariot-text-secondary mt-1 text-xs italic">
            {domains.length > 0 ? domains.join(', ') : 'No domains configured'}
          </span>
        )}
      </div>
    </SettingEditableCard>
  );
};
